import { defineStore } from "pinia";
import type Resident from "../../../backend/app/models/resident";
import { useAuthStore } from "./auth";

export const useResidentStore = defineStore("resident", () => {
  const toast = useToast();
  const authStore = useAuthStore();
  const familyGroupsStore = useGroupStore();
  const residents = ref<Resident[]>([]);
  const activeResidents = useState<any[]>("resident.activeResidents", () => []);
  const residentsLoading = ref(false);
  const residentsError = ref<string | null>(null);

  const needRefresh = useState<boolean>("needRefresh", () => false);
  const findById = (array, id) => array.find((item) => item.id === id);

  // Watch for current group changes and fetch active residents
  watch(
    () => familyGroupsStore.currentSelectedGroup,
    (selectedGroup) => {
      // Only fetch on client side and when we have a valid group
      if (selectedGroup && selectedGroup.id && import.meta.client) {
        fetchActiveResidents(selectedGroup.id);
      }
    },
    { immediate: true },
  );

  async function getResidents(params: any = {}) {
    if (authStore.currentOrganisation) {
      try {
        const allResidents = (await authStore.api("GET", `/resident/index/`, {
          queryString: params,
        })) as any;
        if (allResidents && allResidents.data) {
          allResidents.data.forEach((resident) => {
            resident.birth = resident.birth.substring(0, 10);
            resident.familyGroup = null;

            if (resident.family_group_id) {
              const famGroup = findById(familyGroupsStore.groups, resident.family_group_id);
              resident.familyGroup = famGroup ? famGroup : null;
            }
          });
        }

        return allResidents;
      } catch (error) {
        console.error("Error fetching residents data:", error);
        return { data: [], meta: { total: 0, lastPage: 0, currentPage: 1 } };
      }
    }
    return { data: [], meta: { total: 0, lastPage: 0, currentPage: 1 } };
  }

  async function getResidentProfile(id: number): Promise<Resident> {
    if (!authStore.currentOrganisation) {
      throw createError({
        statusCode: 403,
        statusMessage: "No organisation selected",
      });
    }

    try {
      const result = (await authStore.api("GET", `/resident/show/${id}`)) as any;
      return result as Resident;
    } catch (error) {
      console.error("Error fetching resident profile:", error);
      // Re-throw the error so useAsyncData can handle it properly
      throw error;
    }
  }

  async function getResidentsGroupsCount() {
    if (authStore.currentOrganisation) {
      try {
        const result = (await authStore.api("GET", `/resident/groups/`)) as any;
        return result as Record<string, string>[];
      } catch (error) {
        console.error("Error fetching residents data:", error);
        return null;
      }
    }
    return null;
  }

  async function getLastResidents() {
    if (!authStore.currentOrganisation) return null;

    try {
      const result = (await authStore.api(
        "GET",
        `/resident/last/${familyGroupsStore.currentSelectedGroup?.id}`,
      )) as any;

      const data = result.data.map((resident) => {
        return {
          surname: resident.surname,
          forename: resident.forename,
          gender: resident.gender,
          birth: resident.birth,
          color: findById(familyGroupsStore.groups, resident.family_group_id).color,
          date: resident.created_at.substring(0, 10),
          id: resident.id,
        } as any;
      });
      return data as any;
    } catch (error) {
      console.error("Error fetching last residents data:", error);
      return null;
    }
  }

  async function getActiveResidentsCount() {
    if (!authStore.currentOrganisation) return null;

    try {
      const result = await authStore.api("GET", `/resident/active-count/`);
      return result.total as number;
    } catch (error) {
      console.error("Error fetching active residents count:", error);
      return null;
    }
  }

  async function getResidentsCount() {
    if (!authStore.currentOrganisation) return null;

    try {
      const result = (await authStore.api("GET", `/resident/count/`)) as any;
      return result.total as number;
    } catch (error) {
      console.error("Error fetching residents data:", error);
      return null;
    }
  }

  // Fetch active residents for a specific family group
  async function fetchActiveResidents(groupId: number) {
    try {
      // Check if we're in a proper Nuxt context
      try {
        const nuxtApp = useNuxtApp();
        if (!nuxtApp) {
          console.log("Nuxt app not ready, skipping fetchActiveResidents");
          return;
        }
      } catch (error) {
        console.log("Nuxt app not ready, skipping fetchActiveResidents", error);
        return;
      }

      residentsLoading.value = true;
      residentsError.value = null;

      // Check if auth store is properly initialized
      if (!authStore || typeof authStore.api !== "function") {
        console.log(
          "Auth store not ready or api method not available, skipping fetchActiveResidents",
        );
        residentsLoading.value = false;
        return;
      }

      const response = (await authStore.api("GET", `/resident/active/${groupId}`)) as any;

      // The backend returns the array directly, not wrapped in {data: [...]}
      if (Array.isArray(response)) {
        activeResidents.value = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for wrapped response
        activeResidents.value = response.data;
      } else {
        activeResidents.value = [];
      }
    } catch (err: any) {
      console.error("Error fetching active residents:", err);
      residentsError.value = err.message || "Failed to load residents";
      activeResidents.value = [];
    } finally {
      residentsLoading.value = false;
    }
  }

  // Legacy method for backward compatibility
  async function getActiveResidents() {
    if (!authStore.currentOrganisation) return null;

    try {
      return (await authStore.api(
        "GET",
        `/resident/active/${familyGroupsStore.currentSelectedGroup?.id}`,
      )) as any;
    } catch (error) {
      console.error("Error fetching active residents data:", error);
      return null;
    }
  }

  async function deleteResident(resident_id) {
    if (
      authStore.currentOrganisation &&
      (authStore.user.role == "manager" || authStore.user.role == "owner")
    ) {
      try {
        (await authStore.api("DELETE", `/resident/delete/`, {
          organisation_id: authStore.currentOrganisation,
          resident_id: resident_id,
        })) as any;

        await navigateTo("/residents");
        toast.add({
          title: "Success",
          description: "Resident deleted successfully.",
          icon: "i-heroicons-exclamation-triangle",
          color: "success",
          duration: 5000,
        });
      } catch (error) {
        console.error("Error Deleting resident:", error);
        return null;
      }
    }
    return null;
  }

  async function activateResident(residentId, active, dates) {
    if (
      authStore.currentOrganisation &&
      (authStore.user.role == "manager" || authStore.user.role == "owner")
    ) {
      try {
        const result = (await authStore.api("PUT", `/resident/activate/`, {
          organisation_id: authStore.currentOrganisation,
          resident_id: residentId,
          active: active,
          dates: dates,
        })) as any;
        needRefresh.value = true;
        return result as Resident;
      } catch (error) {
        throw new Error(error.message);
      }
    }
  }

  async function updateResident(payload: Partial<Resident> & { resident_id: number }) {
    if (!authStore.currentOrganisation) return null;

    try {
      const body = {
        ...payload,
        organisation_id: authStore.currentOrganisation,
      } as any;
      const result = (await authStore.api("PUT", `/resident/update/`, body)) as any;
      needRefresh.value = true;
      return result as Resident;
    } catch (error) {
      console.error("Error updating resident:", error);
      throw error;
    }
  }

  /**
   * Extract resident data from an uploaded document.
   * Returns a raw fetch Response so the caller can consume the NDJSON stream.
   */
  async function extractFromDocument(file: File, signal?: AbortSignal): Promise<Response> {
    const formData = new FormData();
    formData.append("document", file);

    return authStore.api(
      "POST",
      "/resident/extract-from-document",
      formData,
      {},
      { stream: true, signal },
    ) as Promise<Response>;
  }

  return {
    residents,
    activeResidents,
    residentsLoading,
    residentsError,
    getResidents,
    getResidentProfile,
    deleteResident,
    activateResident,
    updateResident,
    getResidentsGroupsCount,
    getResidentsCount,
    getLastResidents,
    getActiveResidentsCount,
    getActiveResidents,
    fetchActiveResidents,
    extractFromDocument,
    needRefresh,
  };
});
